import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const SearchGlobalBar = () => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Mock search results - in real app, this would be API calls
  const mockResults = [
    {
      id: 1,
      type: 'lead',
      title: 'Sarah Johnson',
      subtitle: 'TechCorp - CEO',
      description: 'High-quality lead matching your ICP',
      path: '/lead-management',
      icon: 'User'
    },
    {
      id: 2,
      type: 'lead',
      title: 'Michael Chen',
      subtitle: 'DataSoft - CTO',
      description: 'Recently engaged with email campaign',
      path: '/lead-management',
      icon: 'User'
    },
    {
      id: 3,
      type: 'communication',
      title: 'Q4 Outreach Campaign',
      subtitle: 'Email Campaign',
      description: '24% open rate, 156 leads contacted',
      path: '/communication-hub',
      icon: 'Mail'
    },
    {
      id: 4,
      type: 'analytics',
      title: 'Lead Conversion Report',
      subtitle: 'Analytics Dashboard',
      description: 'Monthly performance metrics',
      path: '/analytics-dashboard',
      icon: 'BarChart3'
    },
    {
      id: 5,
      type: 'lead',
      title: 'Jennifer Martinez',
      subtitle: 'CloudTech - VP Sales',
      description: 'Hot lead - 95% match score',
      path: '/lead-management',
      icon: 'User'
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query?.length > 0) {
      setIsLoading(true);
      // Simulate API delay
      const timer = setTimeout(() => {
        const filtered = mockResults?.filter(item =>
          item?.title?.toLowerCase()?.includes(query?.toLowerCase()) ||
          item?.subtitle?.toLowerCase()?.includes(query?.toLowerCase()) ||
          item?.description?.toLowerCase()?.includes(query?.toLowerCase())
        );
        setResults(filtered);
        setIsLoading(false);
        setIsOpen(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setResults([]);
      setIsOpen(false);
      setIsLoading(false);
    }
  }, [query]);

  const handleInputChange = (e) => {
    setQuery(e?.target?.value);
  };

  const handleResultClick = (result) => {
    navigate(result?.path);
    setQuery('');
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  const getResultTypeColor = (type) => {
    switch (type) {
      case 'lead':
        return 'text-accent';
      case 'communication':
        return 'text-secondary';
      case 'analytics':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const getResultTypeBadge = (type) => {
    switch (type) {
      case 'lead':
        return 'Lead';
      case 'communication':
        return 'Campaign';
      case 'analytics':
        return 'Report';
      default:
        return 'Result';
    }
  };

  return (
    <div className="relative w-full" ref={searchRef}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon name="Search" size={16} className="text-muted-foreground" />
        </div>
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Search leads, campaigns, reports..."
          className="w-full pl-10 pr-4 py-2 bg-muted border border-border rounded-md text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-200"
        />
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        )}
      </div>
      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-md shadow-elevation-3 z-dropdown slide-in max-h-96 overflow-y-auto">
          {results?.length === 0 && !isLoading ? (
            <div className="px-4 py-8 text-center">
              <Icon name="Search" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No results found</p>
              <p className="text-xs text-muted-foreground mt-1">
                Try searching for leads, campaigns, or reports
              </p>
            </div>
          ) : (
            <div className="py-2">
              {results?.map((result) => (
                <button
                  key={result?.id}
                  onClick={() => handleResultClick(result)}
                  className="w-full px-4 py-3 text-left hover:bg-muted transition-colors duration-200 focus:outline-none focus:bg-muted"
                >
                  <div className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 ${getResultTypeColor(result?.type)}`}>
                      <Icon name={result?.icon} size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-popover-foreground truncate">
                          {result?.title}
                        </p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          result?.type === 'lead' ? 'bg-accent/10 text-accent' :
                          result?.type === 'communication'? 'bg-secondary/10 text-secondary' : 'bg-primary/10 text-primary'
                        }`}>
                          {getResultTypeBadge(result?.type)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {result?.subtitle}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                        {result?.description}
                      </p>
                    </div>
                    <Icon name="ArrowRight" size={14} className="text-muted-foreground flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Quick Actions Footer */}
          {query?.length > 0 && (
            <div className="px-4 py-3 border-t border-border bg-muted/50">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Press Enter to search all results</span>
                <span>ESC to close</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchGlobalBar;